import React from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { Link } from 'react-router-dom'

import FileUpload from '../../components/form/FileUpload'
import Button from '../../components/form/Button'
import Loading from '../../components/layout/Loading'
import ErrorMessage from '../../components/errors/ErrorMessage'
import QualityChip from '../../components/cv-roaster/CVQualityChip'
import Collapsable from '../../components/layout/Collapsable'

import { useI18N } from '../../../libraries/i18n/hook'
import { useToaster } from '../../../libraries/toaster'
import { uploadUserCV, getUserCv } from '../../../api/users'
import type { CVQuality, CVRoasterResult } from '../../../api/cv-roaster/types'

import type { UserCv } from '../../../api/users/types'
import JoinPremiumButton from '../../components/premium/JoinButton'
import { MetadataLabel } from '../../components/content'

const UserCvSection: React.FC = () => {
  const { t } = useI18N()
  const { showSuccess, showError } = useToaster()

  const {
    data: cv,
    isLoading: isCvLoading,
    error: cvError,
    mutate: refreshCv,
  } = useSWR<UserCv | null>('/users/cv', getUserCv)

  const [selectedCvFile, setSelectedCvFile] = React.useState<File | null>(null)
  const [fileUploadKey, setFileUploadKey] = React.useState(0)

  const {
    trigger: triggerUploadCv,
    isMutating: isUploading,
    error: uploadCvError,
  } = useSWRMutation('/users/cv', uploadUserCV, {
    onSuccess: async (data) => {
      await refreshCv(data, { revalidate: false })
      setSelectedCvFile(null)
      setFileUploadKey((k) => k + 1)
      showSuccess(t('cv.uploaded'))
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : t('cv.error.analysisFailed'))
    },
  })

  const handleUpdateCv = async () => {
    if (!selectedCvFile) return
    await triggerUploadCv({ file: selectedCvFile })
  }

  const showCVAnalysis = () => {
    if(cv) {
      if(cv.pointsOfImprovement?.length) {
        const cvResult: CVRoasterResult = {
          cvId: cv.id,
          cvUrl: cv.srcUrl,
          analysis: {
            CV_quality: (cv.cvQuality as CVQuality) ?? 'low',
            points_of_improvement: cv.pointsOfImprovement,
          },
        }
        return (
          <>
            <div className='flex items-center gap-x-4 bg-soft-green p-2 rounded-sm'>
              <div className='min-w-max whitespace-nowrap'>
                <MetadataLabel icon='ai' strokeColor='deep-green'>
                  <small className='text-deep-green font-semibold'>
                    {t('cv.points.improvement')}
                  </small>
                </MetadataLabel>
              </div>
              <p className='flex-1 text-xs line-clamp-2'>{cv.pointsOfImprovement[0]}</p>
              {cv.pointsOfImprovement.length > 1 && (
                <Link
                  to='/cv-roaster'
                  state={{ result: cvResult }}
                  className='text-xs underline text-deep-green whitespace-nowrap'
                >
                  {t('common.seeMore')}
                </Link>
              )}
            </div>
          </>
        )
      }
      if(cv.requiresPremiumForAnalysis) {
        return (
          <div className='flex items-center gap-x-4'>
            <p className='text-xs'>{t('profile.cv.upgrade.to.analyze')}</p>
            <JoinPremiumButton />
          </div>
        )
      }
      return <p className='text-xs text-gray-600'>{t('cv.no.improvement.points')}</p>
    }
    return null
  }

  if (cvError) return <ErrorMessage message={cvError.toString()} />

  return (
    <section className='w-full'>
      <Collapsable
        size='lg'
        label={(
          <div className='flex-1 flex justify-between items-center pr-2'>
            <span>{t('cv')}</span>
            {cv?.cvQuality && (
              <QualityChip level={cv.cvQuality as CVQuality} size='sm' />
            )}
          </div>
        )}
      >
        <div className='space-y-4'>
          {isCvLoading && <Loading />}
          {!isCvLoading && showCVAnalysis()}

          {!isCvLoading && (
            <FileUpload
              key={fileUploadKey}
              onFileSelected={setSelectedCvFile}
              label=''
              accept='.pdf'
              defaultButtonLabel={{
                select: t('cv.upload.select'),
                change: t('cv.upload.change'),
              }}
              disabled={isUploading}
              defaultFile={
                cv?.srcUrl
                  ? {
                      url: cv.srcUrl,
                      label: t('profile.cv.latest'),
                    }
                  : undefined
              }
              maxMbSize={1}
            />
          )}

          <div className='text-center'>
            {isUploading ? (
              <div className='text-center flex justify-center items-center'>
                <Loading /><small>{t('cv.upload.loadingMessages.processing')}</small>
              </div>
            ): selectedCvFile ? (
              <Button
                type='button'
                onClick={handleUpdateCv}
                disabled={!selectedCvFile || isUploading}
                size='sm'
              >
                {t('common.save')}
              </Button>
            ) : null}
          </div>

          {uploadCvError ? (
            <ErrorMessage
              message={uploadCvError instanceof Error ? uploadCvError.message : uploadCvError.toString()}
            />
          ) : null}
        </div>
      </Collapsable>
    </section>
  )
}

export default UserCvSection

